import type { UseFormReturn } from 'react-hook-form';
import { useState } from 'react';
import { faPalette } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  TextInputField,
} from '@sixui/ui';
import { densityTokens } from '@sixui/ui/themes/base/density.stylex';
import { scaleTokens } from '@sixui/ui/themes/base/scale.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAction } from '~/helpers/getCategoryActionFromType';
import type { IFormMode } from '~/helpers/types';
import type { ILinkFormValues } from './LinkForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { ArticleSelector } from '~/components/organisms/fields/ArticleSelector';
import { CategorySelector } from '~/components/organisms/fields/CategorySelector';
import { FormFileUploader } from '~/components/organisms/fields/FileUploader';
import {
  categoryActionTypes,
  getCategoryActionFromType,
} from '~/helpers/getCategoryActionFromType';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useArticleQuery } from '~/lib/api/modules/Articles/hooks/useArticleQuery';
import { ImageCreatorFormDialog } from '../ImageCreatorForm';

export type ILinkFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<ILinkFormValues>;
  mode: IFormMode;
  appId: string;
  categoryId?: string;
};

const MIN_DENSITY = -1;
const MAX_DENSITY = 0;
const DENSITY = `${densityTokens.interval} * clamp(${MIN_DENSITY}, ${densityTokens.density}, ${MAX_DENSITY}) * ${scaleTokens.scale}`;

const styles = stylex.create({
  linkTypeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridAutoRows: '1fr',
    columnGap: spacingTokens.padding$2,
    rowGap: `calc(${spacingTokens.padding$2} + ${DENSITY})`,
  },
});

export const LinkFormMainSection: React.FC<ILinkFormMainSectionProps> = (
  props,
) => {
  const { sx, form, appId, categoryId } = props;
  const { t } = useTranslation();
  const [imageCreatorOpened, setImageCreatorOpened] = useState(false);
  const appQuery = useAppQuery({ appId });

  const actionType = form.watch('actionType');
  const action = actionType
    ? getCategoryActionFromType(actionType, t)
    : undefined;
  const actionTargetCategoryId = form.watch('actionTargetCategoryId');
  const actionTarget = form.watch('actionTarget');
  const articleId = action?.type === 'article' ? actionTarget : undefined;

  const hasActionTargetCategoryId = !!articleId && !actionTargetCategoryId;
  useArticleQuery(
    hasActionTargetCategoryId ? { appId, articleId } : undefined,
    {
      onSuccess: (data) =>
        form.setValue('actionTargetCategoryId', data.categoriesId?.[0]),
    },
  );

  return (
    <>
      <ImageCreatorFormDialog
        opened={imageCreatorOpened}
        onClose={() => setImageCreatorOpened(false)}
        appId={appId}
        stageSize={{
          width: 540,
          height: 300,
        }}
        defaultValues={{
          backgroundColor:
            appQuery.data?.settings?.press?.env?.appThemeColorPrimary,
        }}
        onSuccess={(id) => {
          form.setValue('pictures', [
            {
              type: 'picture',
              id,
            },
          ]);
        }}
      />

      <FormSection sx={sx}>
        <Labeled label={t('common.name')} required>
          <FormField
            name="name"
            control={form.control}
            as={TextInputField}
            forwardError
          />
        </Labeled>
        <Labeled label={t('LinkFormMainSection.field.picture.label')}>
          <FormFileUploader
            name="pictures"
            control={form.control}
            appId={appId}
            maxFiles={1}
            acceptTypes={['picture']}
            supportingText={t(
              'LinkFormMainSection.field.picture.supporting-text',
            )}
            extraActions={
              <Button
                variant="text"
                onClick={() => setImageCreatorOpened(true)}
                icon={<FontAwesomeIcon icon={faPalette} />}
                loading={appQuery.isLoading}
              >
                {t('LinkFormMainSection.field.picture.designImage')}
              </Button>
            }
          />
        </Labeled>
        <Labeled label={t('LinkFormMainSection.field.parent-category.label')}>
          <FormField
            name="parentId"
            control={form.control}
            as={CategorySelector}
            forwardError
            appId={appId}
            filter={{
              includeTypes: ['category'],
              excludeIds: categoryId ? [categoryId] : undefined,
            }}
          />
        </Labeled>
        <Labeled label={t('LinkFormMainSection.field.type.label')} required>
          <FormField
            name="actionType"
            control={form.control}
            as={RadioGroup}
            rules={{
              onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
                const actionType = event.target.value;
                const action = getCategoryActionFromType(actionType, t);
                form.setValue('actionPrefix', action?.prefix);
                form.setValue('actionTarget', '');
              },
            }}
            sx={styles.linkTypeOptions}
          >
            {categoryActionTypes
              .map((type) => getCategoryActionFromType(type, t))
              .filter((action): action is IAction => !!action)
              .map((action, actionIndex) => (
                <OptionCard
                  key={actionIndex}
                  as={Radio}
                  label={action.label}
                  value={action.type}
                >
                  {action.supportingText}
                </OptionCard>
              ))}
          </FormField>
        </Labeled>

        {action ? (
          action.type === 'article' ? (
            <>
              <Labeled
                label={t('LinkFormMainSection.field.target-category.label')}
              >
                <FormField
                  name="actionTargetCategoryId"
                  control={form.control}
                  as={CategorySelector}
                  forwardError
                  rules={{
                    onChange: () => form.setValue('actionTarget', ''),
                  }}
                  appId={appId}
                  filter={{ includeTypes: ['category'] }}
                />
              </Labeled>
              <Labeled
                label={t('LinkFormMainSection.field.target-article.label')}
                required
              >
                <FormField
                  name="actionTarget"
                  control={form.control}
                  as={ArticleSelector}
                  forwardError
                  appId={appId}
                  categoryId={actionTargetCategoryId}
                />
              </Labeled>
            </>
          ) : (
            <Labeled
              label={t('LinkFormMainSection.field.target.label')}
              required
            >
              <FormField
                name="actionTarget"
                control={form.control}
                as={TextInputField}
                forwardError
                placeholder={action?.placeholder}
                type={action?.textInputFieldType}
                autoComplete={action?.autoComplete}
              />
            </Labeled>
          )
        ) : null}
      </FormSection>
    </>
  );
};
